import { useState } from 'react';
import { useRouter } from 'next/router';
import { Users } from 'lucide-react';
import {
  groupSetupKindKey,
  memberNoteKey,
  setupCardRef,
  symphonyMemberLines,
  whatsappMemberLines,
  type GroupSetupCardData,
  type SetupMemberLine,
} from '../../../../lib/group-setup-card';
import { waGroupScreenPath } from '../../../../lib/wa-group-roster';
import { useWaGroupRoster } from '../../../../lib/use-wa-group-roster';
import { useGroupMembers } from '../../../../lib/use-group-summary';
import { ensureConversationListed } from '../../../../lib/conversations';
import { useOpenConversationInPlace } from '../../../../lib/use-open-side-chat';
import { useBasePath } from '../../../../lib/use-base-path';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { NewGroupDialog } from '../../group-chat/GroupChatDialogs';
import { GroupParticipantsSheet } from '../../group-chat/GroupChatHeader';
import { WhatsAppGlyph } from '../../settings-whatsapp/WhatsAppGlyph';
import { Button, Card, Typography } from '../../ui';
import styles from './GroupSetupCard.module.css';

/**
 * The Maestro's group-setup card (`metadata.groupSetupCard`, `lib/group-setup-card.ts`), drawn in place of the row's
 * fallback line. Before the group exists it is the setup: the kind, the suggested name, the people (one this kind cannot
 * reach says why), and "Review and create", which opens the New group dialog prefilled. Once the server wrote `created`
 * onto the row it is the same card as the group's live status: one line per person with their state, "Open group" and
 * "Group settings". States re-read when the chat opens and on focus; there is no polling loop.
 *
 * Mirrors iOS `GroupSetupCardView` and Android `GroupSetupCard`.
 */
export function GroupSetupCard({ card, conversationId, message }: {
  card: GroupSetupCardData;
  conversationId?: string;
  message: { id?: string; finalId?: string };
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const biFields = { kind: card.kind, created: card.created ? 'yes' : 'no' };
  const seenRef = useItemSeenBi('group-setup-card', { additionalFields: biFields });
  const click = (button: string) => logPageClickBi(
    { page_name: 'chat', button_name: button, ...(conversationId ? { conversation_id: conversationId } : {}) },
    { componentName: button, additionalFields: biFields },
  );
  const kindLine = (
    <Typography variant="p3" component="span" className={styles.kind}>
      {card.kind === 'WHATSAPP' ? <WhatsAppGlyph size={14} /> : <Users size={14} aria-hidden />}
      {t(groupSetupKindKey(card.kind))}
    </Typography>
  );

  return (
    <Card component="section" ref={seenRef} className={styles.card} aria-label={card.created ? card.name : t('chat.groupSetup.title')}
      data-testid="chat-group-setup-card" data-kind={card.kind} data-created={card.created ? 'true' : 'false'}>
      {card.created
        ? <CreatedBody card={card} created={card.created} kindLine={kindLine} onClick={click} />
        : <SetupBody card={card} conversationId={conversationId} message={message} kindLine={kindLine} onClick={click} />}
    </Card>
  );
}

function SetupBody({ card, conversationId, message, kindLine, onClick }: {
  card: GroupSetupCardData;
  conversationId?: string;
  message: { id?: string; finalId?: string };
  kindLine: React.ReactNode;
  onClick: (button: string) => void;
}) {
  const { t } = useTranslation();
  const openInPlace = useOpenConversationInPlace();
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className={styles.head}>
        <Typography variant="h4" component="h3">{t('chat.groupSetup.title')}</Typography>
        {kindLine}
      </div>
      {card.name && <Typography variant="p1" component="p" color="strong">{card.name}</Typography>}
      <MemberList lines={card.members.map((m) => ({ name: m.name, stateKey: memberNoteKey(m, card.kind) }))} />
      <div className={styles.actions}>
        <Button fullWidth onClick={() => { onClick('group_setup_review'); setOpen(true); }} data-testid="group-setup-review">
          {t('chat.groupSetup.review')}
        </Button>
      </div>
      {open && (
        <NewGroupDialog
          setup={{ card, ref: setupCardRef(conversationId, message) }}
          onClose={() => setOpen(false)}
          onOpenGroup={(id) => { setOpen(false); openInPlace(id); }}
        />
      )}
    </>
  );
}

function CreatedBody({ card, created, kindLine, onClick }: {
  card: GroupSetupCardData;
  created: NonNullable<GroupSetupCardData['created']>;
  kindLine: React.ReactNode;
  onClick: (button: string) => void;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const openInPlace = useOpenConversationInPlace();
  const whatsapp = card.kind === 'WHATSAPP';
  const members = useGroupMembers(created.groupConversationId, !whatsapp);
  const roster = useWaGroupRoster(created.groupConversationId, whatsapp);
  const [sheetOpen, setSheetOpen] = useState(false);
  const lines = whatsapp
    ? whatsappMemberLines(card, roster)
    : symphonyMemberLines(card, members.data && !members.data.refusal ? members.data : null);
  const summary = members.data?.summary ?? null;
  const waGroupId = created.waGroupId ?? roster?.waGroupId ?? null;
  const title = (whatsapp ? card.name : summary?.name || card.name) || t('chat.groupSetup.title');

  const openGroup = () => {
    onClick('group_setup_open');
    // Put the group in the chat list now, so it can be reopened from there (a WhatsApp thread may not be listed yet).
    ensureConversationListed({
      id: created.groupConversationId,
      name: title,
      ...(whatsapp ? { communicationChannel: 'WHATSAPP', isWaGroup: true, ...(waGroupId ? { waGroupId } : {}) } : {}),
      updatedDate: new Date().toISOString(),
    });
    openInPlace(created.groupConversationId);
  };
  const openSettings = () => {
    onClick('group_setup_settings');
    if (whatsapp) {
      if (waGroupId) void router.push(`${basePath}${waGroupScreenPath(waGroupId)}`);
      return;
    }
    setSheetOpen(true);
  };

  return (
    <>
      <div className={styles.head}>
        <Typography variant="h4" component="h3">{title}</Typography>
        {kindLine}
      </div>
      {whatsapp && roster?.status === 'CREATING' && (
        <Typography variant="p2" component="p" color="secondary" role="status">{t('chat.groupSetup.status.creating')}</Typography>
      )}
      <MemberList lines={lines} />
      <div className={styles.actions}>
        <Button fullWidth onClick={openGroup} data-testid="group-setup-open">{t('chat.groupSetup.open')}</Button>
        <Button fullWidth variant="outlined" onClick={openSettings} disabled={whatsapp ? !waGroupId : !summary} data-testid="group-setup-settings">
          {t('chat.groupSetup.settings')}
        </Button>
      </div>
      {sheetOpen && summary && (
        <GroupParticipantsSheet summary={summary} conversationId={created.groupConversationId} onClose={() => setSheetOpen(false)} />
      )}
    </>
  );
}

function MemberList({ lines }: { lines: SetupMemberLine[] }) {
  const { t } = useTranslation();
  if (lines.length === 0) return null;
  return (
    <ul className={styles.members}>
      {lines.map((line, i) => (
        <li key={`${line.name}-${i}`} className={styles.member}>
          <Typography variant="p2" component="span" noWrap className={styles.memberName}>{line.name}</Typography>
          {line.stateKey && (
            <Typography variant="p3" component="span" color="secondary" className={styles.state}>{t(line.stateKey)}</Typography>
          )}
        </li>
      ))}
    </ul>
  );
}
